import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Tag, Loader2, RefreshCw, Undo2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/admin/events/price-fix`;

const BatchRow = ({ batch, onRevert, busy }) => (
  <li className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`price-fix-batch-${batch.batch_id}`}>
    <span className="text-gray-600">
      {new Date(batch.applied_at).toLocaleString('es-CL')} · <b>{batch.count}</b> eventos
      {batch.applied_by ? ` · ${batch.applied_by}` : ''}
    </span>
    {batch.reverted ? (
      <span className="text-xs font-semibold text-gray-400">Revertido</span>
    ) : (
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onRevert(batch)} className="rounded-full" data-testid={`price-fix-revert-${batch.batch_id}`}>
        <Undo2 className="w-3.5 h-3.5 mr-1" /> Revertir
      </Button>
    )}
  </li>
);

// Ticketing-site events wrongly saved as free -> "Precio por confirmar". Preview, confirm, revert.
export default function PriceFixCard() {
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null);

  const load = async () => {
    try {
      const [p, b] = await Promise.all([axios.get(`${BASE}/preview`), axios.get(`${BASE}/batches`)]);
      setPreview(p.data);
      setBatches(b.data);
    } catch (e) {
      toast.error('No se pudo cargar la revisión de precios');
    }
  };

  useEffect(() => { load(); }, []);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/apply`, { expected_count: preview.candidate_count });
      toast.success(`${r.data.updated} eventos pasaron a "Precio por confirmar"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar la corrección');
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      load();
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/revert/${revertTarget.batch_id}`);
      toast.success(`${r.data.reverted} eventos restaurados a su valor anterior`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo revertir');
    } finally {
      setBusy(false);
      setRevertTarget(null);
      load();
    }
  };

  const count = preview?.candidate_count ?? 0;

  return (
    <Card className="rounded-2xl border-0 shadow-sm mb-6" data-testid="price-fix-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Tag className="w-5 h-5 text-[#00CC66]" /> Corregir eventos de ticketeras marcados como gratis
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="price-fix-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Eventos con link de venta o Instagram de Passline, PuntoTicket, TicketPlus, Portaldisc, Welcu o Ticketmaster,
          marcados como gratis y sin precio. Pasan a <b>Precio por confirmar</b>. Se guarda el valor anterior para poder revertir.
        </p>
        {!preview ? (
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        ) : (
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-sm" data-testid="price-fix-counts">
              <span className="text-3xl font-extrabold text-[#064E3B]" data-testid="price-fix-candidate-count">{count}</span>
              <span className="text-gray-500"> eventos a corregir · {preview.universe_count} de ticketeras en total</span>
            </div>
            <Button onClick={() => setConfirmOpen(true)} disabled={busy || count === 0} className="btn-primary rounded-full" data-testid="price-fix-apply-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revisar y aplicar
            </Button>
          </div>
        )}
        {preview?.sample?.length > 0 && (
          <ul className="text-xs text-gray-500 list-disc pl-5" data-testid="price-fix-sample">
            {preview.sample.map((s) => <li key={s.id}>{s.name}</li>)}
          </ul>
        )}
        {batches.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Correcciones aplicadas</p>
            <ul className="divide-y divide-gray-100">
              {batches.map((b) => <BatchRow key={b.batch_id} batch={b} onRevert={setRevertTarget} busy={busy} />)}
            </ul>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="price-fix-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Aplicar a {count} eventos?</AlertDialogTitle>
            <AlertDialogDescription>
              Dejarán de mostrarse como "Gratis" y pasarán a "Precio por confirmar". Podrás revertir exactamente estos eventos después.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="price-fix-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={apply} disabled={busy} data-testid="price-fix-confirm-btn">Sí, aplicar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!revertTarget} onOpenChange={(o) => !o && setRevertTarget(null)}>
        <AlertDialogContent data-testid="price-fix-revert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revertir {revertTarget?.count} eventos?</AlertDialogTitle>
            <AlertDialogDescription>Vuelven a su valor anterior de gratis y precio. No se toca ningún otro evento.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="price-fix-revert-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={revert} disabled={busy} data-testid="price-fix-revert-confirm-btn">Sí, revertir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
